import * as THREE from 'three';
import { PALETTE, type PaletteName } from './palette';

/**
 * Gedeelde, gecachete materialen.
 *
 * Eén materiaal per functie, hergebruikt over de hele stad — dat houdt de draw calls laag
 * en dwingt de discipline af uit de art direction: als twee materialen op 200 px niet te
 * onderscheiden zijn, horen ze hetzelfde te zijn.
 *
 * Alles is flat shaded en matte. Er zijn geen texturen in dit project.
 */

const cache = new Map<string, THREE.Material>();

/** Mat, flat-shaded oppervlak. De werkpaardfunctie voor vrijwel alle geometrie. */
export function surface(name: PaletteName): THREE.MeshLambertMaterial {
  return cached(`surface:${name}`, () => {
    const material = new THREE.MeshLambertMaterial({
      color: PALETTE[name],
      flatShading: true,
    });
    material.name = `NA_Mat_${name}`;
    return material;
  });
}

/**
 * Lichtgevend vlak: ramen, neonletters, koplampen. Deze doen in de nachtscène het meeste
 * visuele werk, en ze kosten niets vergeleken met een echte lichtbron.
 */
export function emissive(name: PaletteName, intensity = 1): THREE.MeshBasicMaterial {
  return cached(`emissive:${name}:${intensity}`, () => {
    const material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(PALETTE[name]).multiplyScalar(intensity),
    });
    material.name = `NA_Emit_${name}`;
    return material;
  });
}

/** Halfdoorzichtig oppervlak: ruiten waar je doorheen kijkt, glazen kappen. */
export function glass(name: PaletteName, opacity = 0.35): THREE.MeshLambertMaterial {
  return cached(`glass:${name}:${opacity}`, () => {
    const material = new THREE.MeshLambertMaterial({
      color: PALETTE[name],
      flatShading: true,
      transparent: true,
      opacity,
      depthWrite: false,
    });
    material.name = `NA_Glass_${name}`;
    return material;
  });
}

/**
 * Materiaal dat per-vertex kleuren gebruikt. Nodig zodra meerdere kleurzones tot één
 * gemergede geometrie worden samengevoegd — dan wordt kleur een vertex-attribuut in plaats
 * van een materiaal.
 */
export function vertexColored(): THREE.MeshLambertMaterial {
  return cached('vertexColored', () => {
    const material = new THREE.MeshLambertMaterial({
      vertexColors: true,
      flatShading: true,
    });
    material.name = 'NA_Mat_VertexColors';
    return material;
  });
}

/** Aantal unieke materialen dat op dit moment in gebruik is — bewaakt in de debug-overlay. */
export function materialCount(): number {
  return cache.size;
}

export function disposeMaterials(): void {
  for (const material of cache.values()) material.dispose();
  cache.clear();
}

function cached<T extends THREE.Material>(key: string, create: () => T): T {
  let material = cache.get(key);
  if (!material) {
    material = create();
    cache.set(key, material);
  }
  return material as T;
}
